import * as THREE from 'three';
import { buildWarehouse } from '@/meshkit/buildings/warehouse';
import { buildCanalHouse, buildChurch, buildTenement } from '@/meshkit/buildings/urban';
import { buildHarborProp, type HarborPropKind } from '@/meshkit/props/harbor';
import { buildStreetlamp } from '@/meshkit/props/streetlamp';
import { buildVehicle, type VehicleKind } from '@/meshkit/vehicles/cars';
import { buildCanalBridge } from '@/meshkit/env/ground';
import { worldVertices } from '@/util/verify';
import type { BuildingPlacement } from '@/world/LevelLoader';

/**
 * Voetafdrukken: de enige bron van waarheid over hoe groot iets in de wereld is.
 *
 * Hiervoor stonden dezelfde maten op drie plekken: in de leveldata (`BuildingPlacement`),
 * in de builder (die ze klemde of negeerde) en in handgeschreven tabellen (`SOLID_PROPS`,
 * `solidPropSize()`, `BRIDGE_HALF_WIDTH`). Die drie liepen uit elkaar, en omdat de validator
 * alleen de leveldata met zichzelf vergeleek bleven de tests groen terwijl er kranen in
 * pakhuizen stonden. Alles wat een maat nodig heeft, vraagt hem voortaan hier op.
 *
 * Er zijn bewust **twee** maten per object, want ze beantwoorden verschillende vragen:
 *
 * - `collider` — wat houdt de speler tegen? Alleen massa die tot op loophoogte komt. De giek
 *   van een kraan hangt op acht meter; daar loop je onderdoor, dus die hoort er niet in.
 * - `bounds` — wat neemt het object visueel in beslag? De volledige mesh, giek en dakoverstek
 *   incluis. Dit is de maat waarmee de validator toetst of iets in een gebouw steekt: onder
 *   de vaste isometrische camera leest een giek over een dak als "de kraan zit in de loods",
 *   ook al raakt hij het dak nergens.
 */

/**
 * Tot deze hoogte telt geometrie mee als iets waar je tegenaan loopt. Ruim boven Luca's
 * kruin (1.78 m) zodat een luifel op schouderhoogte nog blokkeert, laag genoeg dat je onder
 * een kraangiek en een uitgestoken lantaarnarm door kunt.
 */
export const WALKABLE_CLEARANCE = 2.2;

/**
 * Hoeveel een mesh buiten zijn romp mag steken zonder dat het een fout is: dakoverstek,
 * plint, gevelbanden, deurluifel. Zichtbaar detail, geen massa om omheen te lopen.
 *
 * De maat wordt bepaald door het diepste authored detail — de luifel boven de laaddeur van
 * de havenloods, die 0.80 m voor de gevel uitsteekt. Ruim genoeg voor trim dus, en nog altijd
 * scherp genoeg om het echte probleem te vangen: de kadesheds werden 2.00 m te diep gebouwd.
 */
export const EAVES_TOLERANCE = 0.9;

/** Asgerichte voetafdruk rond een plaatsingspunt. Het midden hoeft niet de origin te zijn. */
export interface Footprint {
  /** Middelpunt van de doos, relatief aan de plaatsingsorigin, in wereld-assen. */
  readonly offsetX: number;
  readonly offsetZ: number;
  readonly halfX: number;
  readonly halfZ: number;
  /** Bovenkant van de geometrie boven het plaatsingsvlak. Bepaalt of kogels geblokkeerd worden. */
  readonly height: number;
}

interface BuiltFootprint {
  readonly width: number;
  readonly depth: number;
  readonly height: number;
}

const buildingColliderCache = new Map<string, Footprint>();
const buildingBoundsCache = new Map<string, Footprint>();
const propColliderCache = new Map<string, Footprint | null>();
const propBoundsCache = new Map<string, Footprint>();
const localCache = new Map<string, LocalExtents>();

/**
 * Lokale (ongeroteerde) uitersten van een gebouwde asset, gesplitst in de volle mesh en het
 * deel dat tot loophoogte komt.
 */
interface LocalExtents {
  readonly bounds: Box | null;
  readonly walkable: Box | null;
  readonly height: number;
  /** Alleen gebouwen: de romp die de builder zelf opgeeft via `userData.naFootprint`. */
  readonly built: BuiltFootprint | null;
}

interface Box {
  readonly minX: number;
  readonly maxX: number;
  readonly minZ: number;
  readonly maxZ: number;
}

// ── Gebouwen ────────────────────────────────────────────────────────────────────────────

/**
 * Botsingsblok van een gebouw: de romp die de builder daadwerkelijk gebouwd heeft, uit
 * `userData.naFootprint`. Dat veld wordt door de builder gezet uit exact de variabelen
 * waarmee hij bouwt, dus het kan niet van de mesh afwijken.
 */
export function buildingCollider(placement: BuildingPlacement): Footprint {
  const key = buildingKey(placement);
  let cached = buildingColliderCache.get(key);
  if (!cached) {
    const built = localExtentsOf(key, () => buildBuilding(placement)).built;
    if (!built) {
      throw new Error(
        `Builder voor "${placement.kind ?? 'warehouse'}" zet geen userData.naFootprint. `
        + 'Elke gebouwbuilder moet zijn eigen romp opgeven; zie docs/MESHKIT.md.',
      );
    }
    const rotated = rotatedHalfExtents(built.width / 2, built.depth / 2, facingRotation(placement.facing));
    cached = { offsetX: 0, offsetZ: 0, halfX: rotated.halfX, halfZ: rotated.halfZ, height: built.height };
    buildingColliderCache.set(key, cached);
  }
  return cached;
}

/** Volledige gemeten mesh van een gebouw, inclusief dakoverstek, plint en luifels. */
export function buildingBounds(placement: BuildingPlacement): Footprint {
  const key = buildingKey(placement);
  let cached = buildingBoundsCache.get(key);
  if (!cached) {
    const extents = localExtentsOf(key, () => buildBuilding(placement));
    // De builder zet de facing-rotatie al op de group, dus de gemeten uitersten staan al in
    // wereld-assen. Rotatie hoeft hier niet nog eens toegepast te worden.
    cached = footprintFromBox(extents.bounds, extents.height);
    buildingBoundsCache.set(key, cached);
  }
  return cached;
}

/**
 * Bouw het gebouw dat bij een placement hoort. Woont hier en niet in `LevelLoader`, zodat
 * zowel het laden als het meten door dezelfde functie gaat.
 */
export function buildBuilding(placement: BuildingPlacement): THREE.Group {
  const options = {
    width: placement.width,
    depth: placement.depth,
    floors: placement.floors,
    facing: placement.facing,
    wall: placement.wall,
    seed: placement.seed,
  };

  switch (placement.kind ?? 'warehouse') {
    case 'warehouse':
      return buildWarehouse(options);
    case 'canalHouse':
      return buildCanalHouse(options);
    case 'tenement':
      return buildTenement(options);
    case 'church':
      return buildChurch(options);
  }
}

// ── Props ───────────────────────────────────────────────────────────────────────────────

/**
 * Botsingsblok van een prop, of `null` als hij niets op het loopvlak heeft staan — een
 * neonbord hangt aan een gevel, een boot ligt achter de kademuur.
 *
 * Een bolder blijft lager dan `COVER_HEIGHT` in `collision.ts`: je loopt er niet doorheen,
 * maar je schiet er wel overheen. Dat volgt vanzelf uit de gemeten hoogte.
 */
export function propCollider(kind: HarborPropKind, rotation = 0): Footprint | null {
  if (kind === 'neonSign' || kind === 'boat') return null;

  const key = `${kind}@${rotation}`;
  if (propColliderCache.has(key)) return propColliderCache.get(key) ?? null;

  const extents = localExtentsOf(`prop:${kind}`, () => buildHarborProp(kind));
  const footprint = extents.walkable
    ? footprintFromBox(rotateBox(extents.walkable, rotation), extents.height)
    : null;
  propColliderCache.set(key, footprint);
  return footprint;
}

/** Volledige gemeten mesh van een prop — inclusief de giek van een kraan. */
export function propBounds(kind: HarborPropKind, rotation = 0): Footprint {
  const key = `${kind}@${rotation}`;
  let cached = propBoundsCache.get(key);
  if (!cached) {
    const extents = localExtentsOf(`prop:${kind}`, () => buildHarborProp(kind));
    cached = footprintFromBox(rotateBox(extents.bounds, rotation), extents.height);
    propBoundsCache.set(key, cached);
  }
  return cached;
}

// ── Overige wereldobjecten ──────────────────────────────────────────────────────────────

export function streetlampCollider(rotation = 0): Footprint {
  const extents = localExtentsOf('streetlamp', () => buildStreetlamp());
  return footprintFromBox(rotateBox(extents.walkable ?? extents.bounds, rotation), extents.height);
}

export function vehicleBounds(kind: VehicleKind, rotation = 0): Footprint {
  const extents = localExtentsOf(`vehicle:${kind}`, () => buildVehicle(kind));
  return footprintFromBox(rotateBox(extents.bounds, rotation), extents.height);
}

export function bridgeBounds(rotation = 0): Footprint {
  const extents = localExtentsOf('bridge', () => buildCanalBridge());
  return footprintFromBox(rotateBox(extents.bounds, rotation), extents.height);
}

// ── Meten ───────────────────────────────────────────────────────────────────────────────

/**
 * Meet een gebouwde asset één keer en onthoud het resultaat.
 *
 * Bouwen is niet gratis — een pakhuis is ~1900 driehoeken — maar er zijn maar een handvol
 * unieke maatvoeringen per district, dus de cache maakt het verwaarloosbaar.
 */
function localExtentsOf(key: string, build: () => THREE.Object3D): LocalExtents {
  let cached = localCache.get(key);
  if (!cached) {
    cached = measureExtents(build());
    localCache.set(key, cached);
  }
  return cached;
}

/**
 * Splits de vertices in "alles" en "alles wat tot loophoogte komt".
 *
 * Het criterium is de laagste vertex van een onderdeel, niet zijn middelpunt: een paal die
 * van 0.2 tot 7.6 m loopt heeft vertices op y = 0.2 en telt dus mee, terwijl een giek die
 * volledig boven 2.2 m hangt geen enkele vertex onder de grens heeft en wegvalt.
 */
function measureExtents(object: THREE.Object3D): LocalExtents {
  const built = builtFootprintOf(object);
  const points = worldVertices(object);
  if (points.length === 0) return { bounds: null, walkable: null, height: 0, built };

  let bounds: MutableBox | null = null;
  let walkable: MutableBox | null = null;
  let height = -Infinity;

  for (const point of points) {
    bounds = grow(bounds, point.x, point.z);
    if (point.y <= WALKABLE_CLEARANCE) walkable = grow(walkable, point.x, point.z);
    if (point.y > height) height = point.y;
  }

  return { bounds, walkable, height: Math.max(0, height), built };
}

interface MutableBox {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

function grow(box: MutableBox | null, x: number, z: number): MutableBox {
  if (!box) return { minX: x, maxX: x, minZ: z, maxZ: z };
  if (x < box.minX) box.minX = x;
  if (x > box.maxX) box.maxX = x;
  if (z < box.minZ) box.minZ = z;
  if (z > box.maxZ) box.maxZ = z;
  return box;
}

function footprintFromBox(box: Box | null, height: number): Footprint {
  if (!box) return { offsetX: 0, offsetZ: 0, halfX: 0, halfZ: 0, height };
  return {
    offsetX: (box.minX + box.maxX) / 2,
    offsetZ: (box.minZ + box.maxZ) / 2,
    halfX: (box.maxX - box.minX) / 2,
    halfZ: (box.maxZ - box.minZ) / 2,
    height,
  };
}

/**
 * Draai een lokale doos om de Y-as en geef de omhullende asgerichte doos terug.
 *
 * Let op de conventie: `Object3D.rotation.y = θ` beeldt een lokaal punt (x, z) af op
 * (x·cosθ + z·sinθ, −x·sinθ + z·cosθ). Een kraan met `rotation: -π/2` legt zijn gieklengte
 * dus op de wereld-Z-as, en precies dat werd eerder gemist.
 */
function rotateBox(box: Box | null, rotation: number): Box | null {
  if (!box) return null;
  if (rotation === 0) return box;

  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);
  let rotated: MutableBox | null = null;

  for (const x of [box.minX, box.maxX]) {
    for (const z of [box.minZ, box.maxZ]) {
      rotated = grow(rotated, x * cos + z * sin, -x * sin + z * cos);
    }
  }

  return rotated;
}

/** Halve afmetingen van een om de Y-as gedraaide doos die om zijn eigen midden draait. */
export function rotatedHalfExtents(halfWidth: number, halfDepth: number, rotation: number): { halfX: number; halfZ: number } {
  const c = Math.abs(Math.cos(rotation));
  const s = Math.abs(Math.sin(rotation));
  return {
    halfX: halfWidth * c + halfDepth * s,
    halfZ: halfWidth * s + halfDepth * c,
  };
}

function builtFootprintOf(object: THREE.Object3D): BuiltFootprint | null {
  const footprint = object.userData.naFootprint as Partial<BuiltFootprint> | undefined;
  if (
    typeof footprint?.width !== 'number'
    || typeof footprint.depth !== 'number'
    || typeof footprint.height !== 'number'
  ) {
    return null;
  }
  return { width: footprint.width, depth: footprint.depth, height: footprint.height };
}

function buildingKey(placement: BuildingPlacement): string {
  return [
    placement.kind ?? 'warehouse',
    placement.width,
    placement.depth,
    placement.floors,
    placement.facing,
  ].join('|');
}

function facingRotation(facing: BuildingPlacement['facing']): number {
  switch (facing) {
    case 'south':
      return 0;
    case 'north':
      return Math.PI;
    case 'east':
      return -Math.PI / 2;
    case 'west':
      return Math.PI / 2;
  }
}
